import React, { FC, useMemo } from 'react'
import Stripe from 'stripe'
import format from 'date-fns/format'
import { ContractPlanCardHeader, ContractPlanCardBody } from 'components/Card'
import { Spacer } from 'components/Utility'
import { FirebaseDocRefType } from 'firebase/index'
import { ShopSubscriptionType } from 'types/shopSubscription'
import { useSelector } from 'react-redux'
import { State } from 'index'
import { getSubscriptions, getSubscriptionSchedules } from 'reducks/shopSubscription/selectors'

type Props = {
  isMultiSubscriptions: boolean
  isSacriStation: boolean
  shopSubscription: ShopSubscriptionType
  storeRef: FirebaseDocRefType
}

type ContractPlan = {
  amount: number | null
  interval: Stripe.Plan.Interval | null
  nextBillingDate: string | null
  planName: string | null
  status: string | null
}

const ContractPlanCard: FC<Props> = (props) => {
  const { isMultiSubscriptions, isSacriStation, shopSubscription, storeRef } = props

  const subscriptions = useSelector((state: State) => getSubscriptions(state))
  const subscriptionSchedules = useSelector((state: State) => getSubscriptionSchedules(state))

  const { amount, interval, nextBillingDate, planName, status } = useMemo(() => {
    const exception: ContractPlan = {
      amount: null,
      interval: null,
      nextBillingDate: null,
      planName: null,
      status: null,
    }
    const getContractPlan = (subscription: Stripe.Subscription): ContractPlan => {
      const nextBillingDate = format(new Date(subscription.current_period_end * 1000), 'yyyy年M月d日')
      const data = subscription.items.data
      const plan = data[0].plan
      // planが削除されている場合（例外処理）
      if (!plan) {
        return exception
        // 契約終了の場合
      } else if (subscription.cancel_at_period_end || subscription.status === 'canceled') {
        return {
          amount: plan.amount,
          interval: plan.interval,
          nextBillingDate: `請求予定なし`,
          planName: plan.nickname,
          status: subscription.status,
        }
      } else {
        return {
          amount: plan.amount,
          interval: plan.interval,
          nextBillingDate: nextBillingDate,
          planName: plan.nickname,
          status: subscription.status,
        }
      }
    }
    // 継続課金予定
    if (!shopSubscription.stripe_subscription_id || shopSubscription.stripe_subscription_id === '') {
      const subscriptionSchedule = subscriptionSchedules.find((schedule) => {
        return schedule.subscription_schedule.id === shopSubscription.stripe_scheduled_subscription_id
      })
      if (!subscriptionSchedule) return exception
      const subscription = subscriptions.find((subscription) => {
        return subscription.id === subscriptionSchedule.subscription_schedule.subscription
      })
      if (subscription) {
        return getContractPlan(subscription)
      } else {
        const phase = subscriptionSchedule.subscription_schedule.phases[0]
        if (!phase) return exception
        const nextBillingDate = format(new Date(phase.start_date * 1000), 'yyyy年M月d日')
        return {
          amount: subscriptionSchedule.plan.amount,
          interval: subscriptionSchedule.plan.interval,
          nextBillingDate: nextBillingDate,
          planName: subscriptionSchedule.plan.nickname,
          status: subscriptionSchedule.status,
        }
      }
    } else {
      const subscription = subscriptions.find((subscription) => {
        return subscription.id === shopSubscription.stripe_subscription_id
      })
      if (!subscription) return exception
      return getContractPlan(subscription)
    }
  }, [subscriptions, subscriptionSchedules])

  if (status === null || status === 'canceled') return <></>

  return (
    <React.Fragment>
      {isMultiSubscriptions && <ContractPlanCardHeader isSacriStation={isSacriStation} storeRef={storeRef} />}
      <ContractPlanCardBody amount={amount} interval={interval} nextBillingDate={nextBillingDate} planName={planName} />
      {isMultiSubscriptions ? <Spacer size={32} /> : <Spacer size={20} />}
    </React.Fragment>
  )
}

export default ContractPlanCard
